import type Student from 'teacher-ui-client/models/student'
import type { CertificateEventsData } from './student-events/fetchers/certificate-events'

type LegacyCertificateType =
  | 'fast_phonics_peak'
  | 'maths_map'
  | 'maths_mental_minute'
  | 'rex_trophy'
  | 'writing_legends_map'

function mentalMinuteCertificateType(stage: string | undefined, stageType: string | undefined): string {
  switch (stageType) {
    case 'sprint_group':
      return `badges-${stage}-certificate`
    case 'set':
      return `${stage}-certificate`
    case 'complete':
      return `program-certificate`
    default:
      return ''
  }
}

type PartialSchool = {
  accent?: {
    extension?: string
  }
}

export function buildLegacyCertificateUrl(
  product: LegacyProductFp | LegacyProductWl | LegacyProductRex | LegacyProductMs,
  precinct: string,
  certificate: CertificateEventsData,
  student: Omit<Partial<Student>, 'school'> & Pick<Student, 'firstName'> & { school?: PartialSchool },
) {
  let type: LegacyCertificateType
  switch (product) {
    case 'fast_phonics':
      type = 'fast_phonics_peak'
      break
    case 'maths':
      type = precinct === 'mental_minute' ? 'maths_mental_minute' : 'maths_map'
      break
    case 'rex':
      type = 'rex_trophy'
      break
    case 'writing_legends':
      type = 'writing_legends_map'
      break
  }

  const legacyCert: Record<
    LegacyCertificateType,
    { path: string; details: (string | Nullable<number> | undefined)[] }
  > = {
    fast_phonics_peak: {
      path: 'fp',
      details: [
        certificate.legacy_cert_details.lesson,
        certificate.legacy_cert_details.color,
        student.firstName,
        certificate.created_at.getTime(),
      ],
    },
    maths_map: {
      path: 'ms',
      details: [
        certificate.legacy_cert_details.map,
        certificate.legacy_cert_details.color,
        certificate.legacy_cert_details.score,
        student.firstName,
        certificate.created_at.getTime(),
      ],
    },
    maths_mental_minute: {
      path: 'ms-mm',
      details: [
        mentalMinuteCertificateType(certificate.legacy_cert_details.stage, certificate.legacy_cert_details.stage_type),
        student.firstName,
        certificate.created_at.getTime(),
        student.school?.accent?.extension ?? 'au',
      ],
    },
    rex_trophy: {
      path: 'rex',
      details: [certificate.legacy_cert_details.color, student.firstName, certificate.created_at.getTime()],
    },
    writing_legends_map: {
      path: 'wl',
      details: [
        certificate.legacy_cert_details.map,
        certificate.legacy_cert_details.color,
        student.firstName,
        certificate.created_at.getTime(),
      ],
    },
  }

  const asJsonString = JSON.stringify(legacyCert[type].details)
  const encoded = unescape(encodeURIComponent(asJsonString)) // intermediate encoding step, to handle unicode chars, that btoa doesn't accept
  const asBase64 = btoa(encoded)
  const [withoutPadding] = asBase64.split('=')
  const encodedUrlSafe = encodeURI(withoutPadding)

  // just pointing at prod certs server to avoid unnecessary config
  return `https://certificates.readingeggs.com/${legacyCert[type].path}/legacy/${encodedUrlSafe}.svg`
}
